import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Download, FileText, Music, Award, Video } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { VideoEmbed } from "@/components/ProductCard";
import { toYouTubeEmbed } from "@/lib/youtube";

// Tracks 90% watch completion for a single module video via the YouTube
// IFrame API and posts the completion event to /user/progress exactly once.
const ModuleVideoTracker = ({ moduleId, productId, videoUrl, title }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const doneRef = useRef(false);
  useEffect(() => {
    const src = toYouTubeEmbed(videoUrl);
    if (!src || !containerRef.current) return;
    const localKey = `gls_mod_done_${productId}_${moduleId}`;
    if (localStorage.getItem(localKey) === "1") doneRef.current = true;
    // Load the YouTube IFrame API once per session
    const ensure = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve();
      if (!document.querySelector('script[data-yt-iframe-api]')) {
        const s = document.createElement("script");
        s.src = "https://www.youtube.com/iframe_api";
        s.dataset.ytIframeApi = "1";
        document.head.appendChild(s);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(); };
      // In case the API is already loaded but our callback missed
      const iv = setInterval(() => { if (window.YT && window.YT.Player) { clearInterval(iv); resolve(); } }, 200);
    });
    let cancelled = false;
    ensure().then(() => {
      if (cancelled || !containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: (src.match(/embed\/([\w-]{11})/) || [])[1],
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (e) => {
            const YTNS = window.YT.PlayerState;
            if (e.data === YTNS.PLAYING) {
              clearInterval(pollRef.current);
              pollRef.current = setInterval(() => {
                if (doneRef.current) return clearInterval(pollRef.current);
                try {
                  const p = playerRef.current;
                  const dur = p.getDuration();
                  const cur = p.getCurrentTime();
                  if (dur > 0 && cur / dur >= 0.9) markComplete();
                } catch {}
              }, 2000);
            } else if (e.data === YTNS.ENDED) {
              markComplete();
            } else if (e.data === YTNS.PAUSED) {
              clearInterval(pollRef.current);
            }
          },
        },
      });
    });
    const markComplete = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      clearInterval(pollRef.current);
      localStorage.setItem(localKey, "1");
      axios.post(`${API}/user/progress`,
        { product_id: productId, completed_module_id: moduleId },
        { headers: (() => { const t = localStorage.getItem("gls_auth_token"); return t ? { Authorization: `Bearer ${t}` } : {}; })() }
      ).catch(() => {});
    };
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      try { playerRef.current && playerRef.current.destroy(); } catch {}
    };
  }, [moduleId, productId, videoUrl]);
  return (
    <div className="relative w-full aspect-video border border-[#E8DCDD] rounded-sm overflow-hidden bg-black mb-4"
         data-testid={`module-video-${moduleId}`}>
      <div ref={containerRef} className="absolute inset-0 w-full h-full" title={title} />
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 20;

const prettyBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const CourseVideoPanel = ({ manifest }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCourse = manifest?.product_type === "course";
  const isBundle = !!manifest?.is_bundle;
  const productId = manifest?.product_id;

  useEffect(() => {
    if (!isCourse || isBundle || !productId) { setLoading(false); return; }
    axios.get(`${API}/courses/${productId}/settings`)
      .then((r) => setVideoUrl(r.data?.video_embed_url || null))
      .catch(() => setVideoUrl(null))
      .finally(() => setLoading(false));
  }, [isCourse, isBundle, productId]);

  if (!isCourse || isBundle || loading || !videoUrl) return null;

  return (
    <div className="mt-8 text-left" data-testid="course-video-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3 flex items-center gap-2">
        <Video className="w-3.5 h-3.5" /> Your course video
      </div>
      <VideoEmbed url={videoUrl} title={manifest.product_title || "Course"} />
      <p className="text-xs text-[#6B5E62] mb-2">
        Watch the full course video, then work through the downloads below and complete the assessment
        to unlock your signed Certificate of Completion.
      </p>
    </div>
  );
};

const DownloadPanel = ({ session_id, onManifest }) => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/downloads/manifest`, { params: { session_id } })
      .then((r) => { setManifest(r.data); onManifest?.(r.data); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load downloads"));
  }, [session_id, onManifest]);

  const grouped = useMemo(() => {
    if (!manifest?.downloads?.length) return {};
    return manifest.downloads.reduce((acc, d) => {
      (acc[d.kind] = acc[d.kind] || []).push(d);
      return acc;
    }, {});
  }, [manifest]);

  if (error) return <p className="text-sm text-[#8C4A57] mt-6" data-testid="downloads-error">{error}</p>;
  if (!manifest) return <p className="text-sm text-[#6B5E62] mt-6">Preparing your downloads…</p>;

  const sectionOrder = ["ebook", "workbook", "audio", "supplement"];
  const sectionLabel = {
    ebook: "Ebook",
    workbook: "Workbook",
    audio: "Audio (MP3)",
    supplement: "Supplemental Readings",
  };

  const renderRow = (d) => {
    const Icon = d.kind === "audio" ? Music : FileText;
    return (
      <a
        key={d.id}
        href={`${API}/downloads/file/${d.id}?session_id=${encodeURIComponent(session_id)}`}
        data-testid={`download-file-${d.id}`}
        className="group flex items-center gap-4 p-4 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] hover:bg-[#FAF4F5] transition-colors duration-200"
      >
        <div className="flex-shrink-0 w-11 h-11 bg-[#FAF4F5] rounded-sm flex items-center justify-center group-hover:bg-white">
          <Icon className="w-5 h-5 text-[#8C4A57]" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm text-[#2D2628] truncate">{d.filename}</div>
          <div className="text-[10px] text-[#6B5E62] mt-0.5">{prettyBytes(d.size)}</div>
        </div>
        <Download className="w-5 h-5 text-[#8C4A57] group-hover:translate-y-0.5 transition-transform" />
      </a>
    );
  };

  // -------- Modules for a single-course purchase --------
  if (manifest.modules && manifest.modules.length > 0 && !manifest.is_bundle) {
    return (
      <div data-testid="downloads-panel" className="mt-8 text-left">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Your course modules</div>
        <div className="text-xs text-[#6B5E62] mb-6">{manifest.product_title} — {manifest.modules.length} module{manifest.modules.length === 1 ? "" : "s"}</div>
        <div className="space-y-6">
          {manifest.modules.map((m, i) => (
            <section key={m.id} data-testid={`module-${m.id}`} className="border-t border-[#E8DCDD] pt-5">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">Module {i + 1} of {manifest.modules.length}</div>
              <h3 className="text-lg font-semibold text-[#2D2628] mb-2">{m.title}</h3>
              {toYouTubeEmbed(m.video_url) && (
                <ModuleVideoTracker
                  moduleId={m.id}
                  productId={manifest.product_id}
                  videoUrl={m.video_url}
                  title={m.title}
                />
              )}
              {m.description && <p className="text-[13px] text-[#4A3E42] mb-4 leading-relaxed whitespace-pre-line">{m.description}</p>}
              {m.files && m.files.length > 0 ? (
                <div className="space-y-3">{m.files.map(renderRow)}</div>
              ) : (
                <div className="text-xs text-[#6B5E62] italic bg-[#FAF4F5] border border-dashed border-[#E8DCDD] rounded-sm p-3">No files attached yet.</div>
              )}
            </section>
          ))}
        </div>
        {(manifest.downloads || []).filter(d => !manifest.modules.some(m => (m.files || []).some(f => f.id === d.id))).length > 0 && (
          <div className="mt-8 pt-6 border-t border-[#E8DCDD]">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">Additional downloads</div>
            {sectionOrder.filter((k) => grouped[k]?.length).map((k) => {
              const rows = grouped[k].filter(d => !manifest.modules.some(m => (m.files || []).some(f => f.id === d.id)));
              if (!rows.length) return null;
              return (
                <div key={k} className="mb-5">
                  <div className="text-[11px] tracking-[0.24em] uppercase text-[#2D2628] font-semibold mb-2">{sectionLabel[k]}</div>
                  <div className="space-y-3">{rows.map(renderRow)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // -------- Bundle purchase: render per-member-course sections --------
  if (manifest.is_bundle && Array.isArray(manifest.member_courses) && manifest.member_courses.length) {
    return (
      <div data-testid="downloads-panel" className="mt-8 text-left">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Your bundle contents</div>
        <div className="text-xs text-[#6B5E62] mb-6">
          {manifest.product_title} — {manifest.member_courses.length} courses included
        </div>

        <div className="space-y-10">
          {manifest.member_courses.map((mc, idx) => {
            const mcGrouped = (mc.downloads || []).reduce((acc, d) => {
              (acc[d.kind] = acc[d.kind] || []).push(d);
              return acc;
            }, {});
            const hasAnything = mc.video_embed_url || (mc.downloads || []).length;
            return (
              <section
                key={mc.product_id}
                data-testid={`bundle-course-${mc.product_id}`}
                className="border-t border-[#E8DCDD] pt-6"
              >
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">
                  Course {idx + 1} of {manifest.member_courses.length}
                </div>
                <h3 className="text-lg font-semibold text-[#2D2628] mb-3">{mc.title}</h3>

                {mc.video_embed_url && (
                  <div className="mb-5" data-testid={`bundle-video-${mc.product_id}`}>
                    <VideoEmbed url={mc.video_embed_url} title={mc.title} />
                  </div>
                )}

                {!hasAnything && (
                  <div className="text-xs text-[#6B5E62] italic bg-[#FAF4F5] border border-dashed border-[#E8DCDD] rounded-sm p-4">
                    Course materials are being prepared and will appear here shortly.
                  </div>
                )}

                {sectionOrder.filter((k) => mcGrouped[k]?.length).map((k) => (
                  <div key={k} className="mb-5" data-testid={`bundle-section-${mc.product_id}-${k}`}>
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[#2D2628] font-semibold mb-2">
                      {sectionLabel[k]}{mcGrouped[k].length > 1 ? ` (${mcGrouped[k].length})` : ""}
                    </div>
                    <div className="space-y-3">{mcGrouped[k].map(renderRow)}</div>
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      </div>
    );
  }

  // -------- Non-bundle purchase (unchanged flat rendering) --------
  if (!manifest.downloads.length) {
    return (
      <div data-testid="downloads-empty" className="text-sm text-[#6B5E62] mt-6 bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-5">
        Your files are being prepared and will be emailed shortly.
      </div>
    );
  }

  return (
    <div data-testid="downloads-panel" className="mt-8 text-left">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Your downloads</div>
      <div className="text-xs text-[#6B5E62] mb-5">{manifest.product_title}</div>
      {sectionOrder.filter((k) => grouped[k]?.length).map((k) => (
        <div key={k} className="mb-6" data-testid={`download-section-${k}`}>
          <div className="text-[11px] tracking-[0.24em] uppercase text-[#2D2628] font-semibold mb-2">
            {sectionLabel[k]}{grouped[k].length > 1 ? ` (${grouped[k].length})` : ""}
          </div>
          <div className="space-y-3">{grouped[k].map(renderRow)}</div>
        </div>
      ))}
    </div>
  );
};

const CourseQuizPanel = ({ session_id, manifest, onPassed }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [learnerName, setLearnerName] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyPassed, setAlreadyPassed] = useState(false);

  // Only render for course purchases
  const isCourse = manifest?.product_type === "course";
  const productId = manifest?.product_id;

  useEffect(() => {
    if (!isCourse || !productId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/courses/${productId}/quiz`).catch(() => ({ data: null })),
      axios.get(`${API}/courses/${productId}/completion`, { params: { session_id } })
        .catch(() => ({ data: { completed: false } })),
    ]).then(([q, c]) => {
      setQuiz(q.data);
      if (c.data?.completed) {
        setAlreadyPassed(true);
        setResult({ percentage: c.data.percentage, passed: true,
                     certificate_id: c.data.certificate_id });
      }
    }).finally(() => setLoading(false));
  }, [isCourse, productId, session_id]);

  const handleAnswer = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
  };

  const handleSubmit = async () => {
    if (!learnerName.trim()) return toast.error("Please enter the name to print on your certificate");
    if (!quiz) return;
    const unanswered = quiz.questions.filter((q) => !answers[q.id]).length;
    if (unanswered > 0) return toast.error(`Please answer all ${quiz.questions.length} questions`);
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/courses/${productId}/quiz/submit`, {
        session_id, learner_name: learnerName.trim(), answers,
      });
      setResult(resp.data);
      if (resp.data.passed) {
        toast.success(`You passed with ${resp.data.percentage}%! Certificate on its way to your inbox.`);
        if (typeof window.gtag === "function") {
          window.gtag("event", "course_completed", {
            course_id: productId, session_id, score: resp.data.percentage,
          });
        }
        onPassed?.();
      } else {
        toast.error(`Score ${resp.data.percentage}% — you need ${resp.data.passing_score}% to pass. Try again.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const handleRetry = () => {
    setResult(null);
    setAnswers({});
  };

  if (!isCourse) return null;
  if (loading) return (
    <div className="mt-12 pt-10 border-t border-[#E8DCDD] text-center text-sm text-[#6B5E62]" data-testid="course-quiz-panel">
      Loading course assessment…
    </div>
  );
  if (!quiz) return null;

  return (
    <div className="mt-12 pt-10 border-t border-[#E8DCDD] text-left" data-testid="course-quiz-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3 text-center">
        Course completion
      </div>
      <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] mb-3 text-center">
        {quiz.title}
      </h3>
      <p className="text-sm text-[#6B5E62] text-center max-w-lg mx-auto mb-8">
        Pass with {quiz.passing_score}% or higher and we&apos;ll email you a signed
        Certificate of Completion. You can retake the quiz until you pass.
      </p>

      {(result?.passed || alreadyPassed) ? (
        <div className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6 max-w-xl mx-auto text-center" data-testid="course-quiz-passed">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Passed</div>
          <div className="text-4xl font-serif font-semibold text-[#2D2628] mb-3">
            {result?.percentage}%
          </div>
          <p className="text-sm text-[#4A3E42] mb-4">
            Congratulations — your Certificate of Completion is on its way to your inbox
            (Certificate&nbsp;ID <strong>{result?.certificate_id}</strong>). You can also download it
            using the button below.
          </p>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-8" data-testid="course-quiz-form">
          <div>
            <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-2 block">
              Full name (for the certificate)
            </label>
            <input type="text" value={learnerName} onChange={(e) => setLearnerName(e.target.value)}
              placeholder="e.g. Jane Alexandra Doe" data-testid="quiz-learner-name"
              className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#8C4A57]" />
          </div>

          {quiz.questions.map((q, i) => {
            const wasCorrect = result?.per_question?.find(r => r.question_id === q.id)?.correct;
            const answered = !!answers[q.id];
            return (
              <div key={q.id} data-testid={`quiz-question-${i}`}
                className={`border rounded-sm p-5 transition-colors ${
                  result && wasCorrect === false ? "border-red-300 bg-red-50/40"
                  : result && wasCorrect ? "border-green-300 bg-green-50/40"
                  : "border-[#E8DCDD] bg-white"}`}>
                <div className="flex gap-3">
                  <div className="text-[#8C4A57] font-serif font-semibold text-sm mt-0.5">Q{i + 1}</div>
                  <div className="flex-1">
                    <p className="text-[#2D2628] mb-3 leading-relaxed">{q.prompt}</p>
                    {q.type === "multiple_choice" ? (
                      <div className="space-y-2">
                        {q.options.map((opt, oi) => (
                          <label key={oi} data-testid={`quiz-q${i}-opt${oi}`}
                            className={`flex items-start gap-3 cursor-pointer border rounded-sm px-3 py-2 transition-colors ${
                              answers[q.id] === String(oi)
                                ? "border-[#8C4A57] bg-[#FAF4F5]"
                                : "border-[#E8DCDD] hover:border-[#c9a5ad]"}`}>
                            <input type="radio" name={q.id}
                              checked={answers[q.id] === String(oi)}
                              onChange={() => handleAnswer(q.id, String(oi))}
                              disabled={!!result} className="mt-1 accent-[#8C4A57]" />
                            <span className="text-sm text-[#2D2628]">{opt}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <textarea rows={2} value={answers[q.id] || ""}
                        onChange={(e) => handleAnswer(q.id, e.target.value)}
                        disabled={!!result} data-testid={`quiz-q${i}-input`}
                        placeholder="Type your answer…"
                        className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#8C4A57] disabled:bg-[#FAF4F5]" />
                    )}
                    {result && (
                      <div className={`text-xs mt-2 font-semibold ${wasCorrect ? "text-green-700" : "text-red-700"}`}>
                        {wasCorrect ? "✓ Correct" : "✗ Not quite"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {result && !result.passed ? (
            <div className="text-center pt-2">
              <div className="text-sm text-[#6B5E62] mb-3">
                You scored <strong className="text-[#8C4A57]">{result.percentage}%</strong> — you need {result.passing_score}% to pass.
              </div>
              <button onClick={handleRetry} data-testid="quiz-retry-btn" className="btn-primary">
                Try again
              </button>
            </div>
          ) : (
            <div className="pt-2 text-center">
              <button onClick={handleSubmit} disabled={submitting}
                data-testid="quiz-submit-btn" className="btn-primary">
                {submitting ? "Grading…" : "Submit assessment"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CertificatePanel = ({ session_id, manifest }) => {
  const [downloading, setDownloading] = useState(false);
  const [nameOverride, setNameOverride] = useState("");

  // Only render for course purchases
  if (!manifest || manifest.product_type !== "course") return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = new URL(`${API}/certificates/${session_id}`);
      if (nameOverride.trim()) url.searchParams.set("name", nameOverride.trim());
      const resp = await axios.get(url.toString(), { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `Certificate-${manifest.product_title.replace(/[^a-z0-9]/gi, "_").slice(0, 50)}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success("Certificate downloaded");
      if (typeof window.gtag === "function") {
        window.gtag("event", "certificate_download", {
          course_id: manifest.product_id, session_id,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Certificate generation failed");
    } finally { setDownloading(false); }
  };

  return (
    <div className="mt-12 pt-10 border-t border-[#E8DCDD] text-left" data-testid="certificate-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3 text-center">
        Recognition of completion
      </div>
      <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] mb-3 text-center">
        Your Certificate of Completion
      </h3>
      <p className="text-sm text-[#6B5E62] text-center max-w-lg mx-auto mb-6">
        A personalised Certificate of Completion signed by Dr. Andrew Campbell, complete with
        the Global Leader Solutions crest and today&apos;s completion date.
      </p>
      <div className="max-w-md mx-auto bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6">
        <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-2 block">
          Name to print on certificate (optional)
        </label>
        <input type="text" value={nameOverride} onChange={(e) => setNameOverride(e.target.value)}
          placeholder="Leave blank to use billing name" data-testid="certificate-name-input"
          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white mb-4 text-sm focus:outline-none focus:border-[#8C4A57]" />
        <button onClick={handleDownload} disabled={downloading}
          data-testid="certificate-download-btn"
          className="btn-primary w-full justify-center">
          <Award className="w-4 h-4 mr-2" />
          {downloading ? "Generating…" : "Download Certificate (PDF)"}
        </button>
      </div>
    </div>
  );
};

const PactBonusOptIn = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email, source: "post-purchase-pact" });
      setSent(true);
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { method: "post-purchase-pact" });
      }
    } finally { setBusy(false); }
  };
  if (sent) return (
    <div className="mt-10 pt-10 border-t border-[#E8DCDD] text-center" data-testid="pact-optin-thanks">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Bonus sent</div>
      <p className="text-[#6B5E62] text-sm">Check your inbox — your free P.A.C.T. Framework preview is on its way.</p>
    </div>
  );
  return (
    <div className="mt-10 pt-10 border-t border-[#E8DCDD] text-center" data-testid="pact-optin-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Bonus for you</div>
      <h3 className="text-2xl font-serif font-semibold text-[#2D2628] mb-3">
        Get the free P.A.C.T. Framework
      </h3>
      <p className="text-sm text-[#6B5E62] max-w-md mx-auto mb-6">
        A free preview of Dr. Campbell&apos;s neuroscience-grounded framework for shifting organizational
        discourse from defensive to collaborative. Enter your email to receive it.
      </p>
      <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="pact-optin-form">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email" data-testid="pact-optin-email"
          className="flex-1 px-4 py-3 border border-[#E8DCDD] rounded-sm text-[#2D2628] focus:outline-none focus:border-[#8C4A57]" />
        <button type="submit" disabled={busy} data-testid="pact-optin-submit"
          className="btn-primary">
          {busy ? "Sending…" : "Send me the ebook"}
        </button>
      </form>
    </div>
  );
};

const UpsellPanel = ({ manifest }) => {
  const [products, setProducts] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  const purchasedId = manifest?.product_id;
  const suggestions = useMemo(() => {
    if (!products.length) return [];
    const filtered = products.filter((p) => p.id !== purchasedId);
    const flagshipOrder = [
      "c_leadership",
      "c_mega_bundle",
      "eb_mega_bundle",
      "eb_leadership_bundle",
      "eb_pact_framework",
      "eb_conflict_mgmt",
      "eb_self_1",
    ];
    const pick = flagshipOrder.map((id) => filtered.find((p) => p.id === id)).filter(Boolean);
    const extras = filtered.filter((p) => !flagshipOrder.includes(p.id) && p.is_bundle);
    return [...pick, ...extras].slice(0, 3);
  }, [products, purchasedId]);

  const resolveCover = (u) =>
    typeof u === "string" && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  const handleAdd = (p) => {
    addItem(p);
    toast.success(`Added — ${p.title}`);
    if (typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency: "USD", value: p.price, source: "post-purchase-upsell",
        items: [{ item_id: p.id, item_name: p.title, price: p.price, quantity: 1 }],
      });
    }
  };

  if (!suggestions.length) return null;

  return (
    <div className="mt-12 pt-10 border-t border-[#E8DCDD] text-left" data-testid="upsell-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3 text-center">
        Complete your leadership journey
      </div>
      <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] mb-3 text-center">
        Keep the momentum going
      </h3>
      <p className="text-sm text-[#6B5E62] text-center max-w-lg mx-auto mb-8">
        Buyers who invest in one Global Leader Solutions program typically pair it with these next.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {suggestions.map((p) => (
          <div key={p.id} data-testid={`upsell-${p.id}`}
            className="bg-white border border-[#E8DCDD] rounded-md overflow-hidden flex flex-col">
            <div className="aspect-[4/5] overflow-hidden bg-[#FAF4F5]">
              <img src={resolveCover(p.cover)} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-4 flex flex-col flex-1">
              <div className="text-[9px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                {p.category_label}
              </div>
              <h4 className="text-sm font-serif font-semibold text-[#2D2628] mb-2 leading-snug flex-1">
                {p.title.length > 60 ? p.title.slice(0, 60) + "…" : p.title}
              </h4>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-lg font-serif font-semibold text-[#2D2628]">
                  ${p.price.toFixed(2)}
                </span>
                {p.regular_price && p.regular_price > p.price && (
                  <span className="text-xs text-[#B79CA1] line-through">
                    ${p.regular_price.toFixed(2)}
                  </span>
                )}
              </div>
              <button data-testid={`upsell-add-${p.id}`} onClick={() => handleAdd(p)}
                className="btn-outline text-xs justify-center w-full">
                Add to cart
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link to="/cart" data-testid="upsell-cart-link" className="btn-primary inline-flex text-xs">
          Go to cart
        </Link>
      </div>
    </div>
  );
};

// Post-purchase panel for the $6.99 P.A.C.T. Framework ebook mini offer.
// Fetches the PDF path from a paid-session-verified endpoint and offers a
// direct download link — bypasses the course-only manifest/quiz/cert panels.
const PactMiniSuccessPanel = ({ session_id }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/downloads/pact-mini`, { params: { session_id } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load your download"));
  }, [session_id]);
  return (
    <div className="mt-8 max-w-xl mx-auto text-left" data-testid="pact-mini-success-panel">
      <div className="border border-[#E8DCDD] bg-[#FAF4F5] rounded-sm p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Your ebook</div>
        <h3 className="font-serif text-2xl text-[#2D2628] mb-2">The P.A.C.T. Framework</h3>
        <p className="text-sm text-[#4A3E42] mb-5">
          Dr. Andrew Campbell&apos;s neuroscience-grounded framework for shifting
          workplace discourse from defensive to collaborative. Save the PDF —
          we&apos;ve also emailed you a copy of the receipt.
        </p>
        {error && <p className="text-sm text-[#8C4A57]" data-testid="pact-mini-error">{error}</p>}
        {data?.url && (
          <a
            href={data.url}
            target="_blank"
            rel="noopener"
            download={data.filename || "PACT-Framework-Guide.pdf"}
            data-testid="pact-mini-download-link"
            className="inline-flex items-center gap-2 bg-[#8C4A57] text-white text-[13px] tracking-[0.14em] uppercase font-semibold px-6 py-3 rounded-sm hover:bg-[#6f3945] transition"
          >
            <Download className="w-4 h-4" /> Download the ebook
          </a>
        )}
        {!data && !error && <p className="text-sm text-[#6B5E62]">Preparing your download…</p>}
      </div>
    </div>
  );
};

// Post-purchase panel for the reusable per-course $6.99 companion ebook offer.
// The href points at a session-gated FastAPI endpoint that streams the PDF —
// the underlying storage URL is never exposed to the client.
const CourseEbookSuccessPanel = ({ session_id }) => {
  const href = `${API}/downloads/course-ebook?session_id=${encodeURIComponent(session_id)}`;
  return (
    <div className="mt-8 max-w-xl mx-auto text-left" data-testid="course-ebook-success-panel">
      <div className="border border-[#E8DCDD] bg-[#FAF4F5] rounded-sm p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Your ebook</div>
        <h3 className="font-serif text-2xl text-[#2D2628] mb-2">Your companion ebook is ready</h3>
        <p className="text-sm text-[#4A3E42] mb-5">
          Save the PDF to your device — we&apos;ve also emailed you a copy of the
          receipt. Your download link stays live from your secure order page.
        </p>
        <a
          href={href}
          target="_blank"
          rel="noopener"
          data-testid="course-ebook-download-link"
          className="inline-flex items-center gap-2 bg-[#8C4A57] text-white text-[13px] tracking-[0.14em] uppercase font-semibold px-6 py-3 rounded-sm hover:bg-[#6f3945] transition"
        >
          <Download className="w-4 h-4" /> Download the ebook
        </a>
      </div>
    </div>
  );
};

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);
  const { clear } = useCart();
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    if (!session_id) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await getPaymentStatus(session_id);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          // Kick off the welcome + magic-login email (fire-and-forget, idempotent server-side)
          axios.post(`${API}/payments/welcome-email`, { session_id })
            .catch((err) => console.warn("welcome-email trigger failed", err));
          // Google Ads purchase conversion — fire once per session_id
          try {
            const firedKey = `gls_gads_purchase_${session_id}`;
            if (typeof window.gtag === "function" && !sessionStorage.getItem(firedKey)) {
              const value = typeof data.amount_total === "number"
                ? Number((data.amount_total / 100).toFixed(2))
                : undefined;
              const currency = (data.currency || "USD").toUpperCase();
              window.gtag("event", "conversion", {
                send_to: "AW-7763993923/5UA-CMPKlPYcEIKnoNdE",
                transaction_id: session_id,
                value,
                currency,
              });
              sessionStorage.setItem(firedKey, "1");
            }
          } catch (e) { /* non-blocking */ }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= POLL_MAX_ATTEMPTS) { setStatus("timeout"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [session_id]);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-4 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-[#8C4A57] animate-spin mx-auto mb-6" />
          <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-3">Confirming your order…</h1>
          <p className="text-[#6B5E62]">This will just take a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle className="w-14 h-14 text-[#8C4A57] mx-auto mb-6" />
          <h1 className="text-4xl font-serif font-semibold text-[#2D2628] mb-3" data-testid="payment-success-heading">Thank you</h1>
          <p className="text-[#6B5E62] mb-1">Your purchase is confirmed.</p>
          <p className="text-sm text-[#6B5E62] mb-2">A receipt has been emailed to you.</p>

          {detail?.lookup_key === "course_ebook" ? (
            <CourseEbookSuccessPanel session_id={session_id} />
          ) : detail?.lookup_key === "pact_mini" ? (
            <PactMiniSuccessPanel session_id={session_id} />
          ) : (
            <>
              <CourseVideoPanel manifest={manifest} />
              <DownloadPanel session_id={session_id} onManifest={setManifest} />
              <CourseQuizPanel session_id={session_id} manifest={manifest} />
              <CertificatePanel session_id={session_id} manifest={manifest} />
              <PactBonusOptIn />
              <UpsellPanel manifest={manifest} />
            </>
          )}

          <div className="mt-10 flex justify-center gap-3">
            <Link to="/" className="btn-outline" data-testid="success-home-link">Back to home</Link>
            <Link to="/courses" className="btn-primary" data-testid="success-courses-link">Explore more</Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "error") && (
        <>
          <XCircle className="w-14 h-14 text-[#8C4A57] mx-auto mb-6" />
          <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-3">Something went wrong</h1>
          <p className="text-[#6B5E62] mb-8">
            {detail?.payment_status ? `Status: ${detail.payment_status}` : "We could not confirm your payment."}
          </p>
          <Link to="/cart" className="btn-primary" data-testid="failure-cart-link">Return to cart</Link>
        </>
      )}
      {status === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 text-[#8C4A57] mx-auto mb-6" />
          <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-3">Still processing</h1>
          <p className="text-[#6B5E62] mb-8">Your payment is taking longer than expected. Check your email — you will hear from us shortly.</p>
          <Link to="/" className="btn-outline">Back to home</Link>
        </>
      )}
    </div>
  );
};

export const PaymentCancel = () => (
  <div data-testid="payment-cancel-page" className="max-w-2xl mx-auto px-4 py-24 text-center">
    <XCircle className="w-14 h-14 text-[#8C4A57] mx-auto mb-6" />
    <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-3">Checkout canceled</h1>
    <p className="text-[#6B5E62] mb-8">No charge was made. Your cart is still saved.</p>
    <Link to="/cart" className="btn-primary" data-testid="cancel-cart-link">Return to cart</Link>
  </div>
);
